import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Slider,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  FileDownloadOutlined as DownloadIcon,
  GraphicEq as WaveformIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Replay as ReplayIcon,
  SkipPrevious as SkipPrevIcon,
  SkipNext as SkipNextIcon,
  Repeat as RepeatIcon,
  VolumeUp as VolumeIcon,
  VolumeOff as MuteIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import SpeciesConfirmationCard from './SpeciesConfirmationCard';

// @ts-ignore
import WaveSurfer from 'wavesurfer.js';
// @ts-ignore
import Spectrogram from 'wavesurfer.js/dist/plugins/spectrogram.esm.js';
import { resolveAudioUrl } from '../resolveAudioUrl';

const formatSeconds = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

interface Detection {
  name: string;
  scientificName: string;
  confidence: number;
  imageUrl: string;
}

interface ProcessedFile {
  fileName: string;
  timestamp: string;
  duration: string;
  status: 'ANALYZED' | 'PROCESSING' | 'FAILED';
  clipData?: Record<string, unknown>;
  detections?: Detection[];
  deploymentId?: string;
  fileId?: string;
}

interface AudioAnalyzerPopupProps {
  open: boolean;
  onClose: () => void;
  file: ProcessedFile | null;
  deploymentId?: string;
  fileId?: string;
  /** Saves the file's audio; without it the popup offers no download. */
  onDownload?: (file: ProcessedFile) => Promise<void>;
}

const AudioAnalyzerPopup: React.FC<AudioAnalyzerPopupProps> = ({
  open,
  onClose,
  file,
  deploymentId,
  fileId,
  onDownload,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState('00:00');
  const [duration, setDuration] = useState('00:00');
  const [audioUnavailable, setAudioUnavailable] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const canDownload = Boolean(onDownload && (file?.clipData?.url || file?.fileId));

  const handleDownload = async () => {
    if (!file || !onDownload || isDownloading) return;
    setIsDownloading(true);
    setDownloadFailed(false);
    try {
      await onDownload(file);
    } catch (err) {
      console.error('Failed to download audio:', err);
      setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const waveformRef = useRef<HTMLDivElement>(null);
  const spectrogramRef = useRef<HTMLDivElement>(null);
  const spectroPlayheadRef = useRef<HTMLDivElement>(null);
  const wavesurfer = useRef<WaveSurfer | null>(null);

  useEffect(() => {
    if (!open || !file) return;
    setAudioUnavailable(false);
    setDownloadFailed(false);

    let initTimeout: ReturnType<typeof setTimeout>;

    const initWaveSurfer = () => {
      if (waveformRef.current && spectrogramRef.current) {
        const audio = new Audio();
        audio.crossOrigin = 'anonymous';

        wavesurfer.current = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: 'rgba(16, 185, 129, 0.4)',
          progressColor: '#10b981',
          cursorColor: '#fff',
          cursorWidth: 2,
          height: 'auto',
          normalize: true,
          barWidth: 2,
          barGap: 1,
          barRadius: 2,
          media: audio,
          fetchParams: {
            mode: 'cors',
          },
          plugins: [
            Spectrogram.create({
              container: spectrogramRef.current,
              labels: true,
              height: 256,
              splitChannels: false,
              labelsBackground: 'rgba(0, 0, 0, 0.5)',
              labelsColor: '#10b981',
              fftSamples: 1024,
            }),
          ],
        });

        resolveAudioUrl(file)
          .then((audioUrl) => {
            if (!audioUrl) {
              setAudioUnavailable(true);
              return undefined;
            }
            return wavesurfer.current?.load(audioUrl);
          })
          .catch(() => setAudioUnavailable(true));

        wavesurfer.current.on('ready', () => {
          setDuration(formatSeconds(wavesurfer.current?.getDuration() || 0));
          if (spectroPlayheadRef.current) {
             spectroPlayheadRef.current.style.display = 'block';
             spectroPlayheadRef.current.style.left = '0%';
          }
          setIsMuted(false);
          setVolume(80);
          wavesurfer.current?.setVolume(0.8);
        });

        wavesurfer.current.on('timeupdate', (time: number) => {
          setCurrentTime(formatSeconds(time));
          const dur = wavesurfer.current?.getDuration() || 1;
          if (spectroPlayheadRef.current) {
             spectroPlayheadRef.current.style.left = `${(time / dur) * 100}%`;
          }
        });

        wavesurfer.current.on('play', () => setIsPlaying(true));
        wavesurfer.current.on('pause', () => setIsPlaying(false));
        wavesurfer.current.on('error', (err: unknown) => {
          console.error('WaveSurfer Error:', err);
          setAudioUnavailable(true);
        });
      } else {
        initTimeout = setTimeout(initWaveSurfer, 100);
      }
    };

    initWaveSurfer();

    return () => {
      clearTimeout(initTimeout);
      if (wavesurfer.current) {
        wavesurfer.current.destroy();
        wavesurfer.current = null;
      }
      setIsPlaying(false);
      setCurrentTime('00:00');
      setDuration('00:00');
    };
  }, [open, file]);

  const togglePlay = () => {
    if (audioUnavailable) return;
    if (wavesurfer.current) {
      wavesurfer.current.playPause();
    }
  };

  const toggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (wavesurfer.current) {
      wavesurfer.current.setMuted(nextMuted);
    }
  };

  const handleVolumeChange = (_: Event, val: number | number[]) => {
    const newVol = val as number;
    setVolume(newVol);
    if (isMuted && newVol > 0) {
      setIsMuted(false);
      wavesurfer.current?.setMuted(false);
    }
    if (wavesurfer.current) {
      wavesurfer.current.setVolume(newVol / 100);
    }
  };

  if (!file) return null;

  const detections: Detection[] = file.clipData ? [{
    name: (file.clipData.common_name as string) || 'Unknown Species',
    scientificName: (file.clipData.scientific_name as string) || 'Unknown',
    confidence: file.clipData.confidence ? Math.round(Number(file.clipData.confidence) * 100) : 0,
    imageUrl: (file.clipData.image_url as string) || 'https://placehold.co/40x40/10b981/000000?text=IMG',
  }] : (file.detections || []);

  const detectedSpeciesName = detections[0]?.name || detections[0]?.scientificName;
  const activeDeploymentId = deploymentId || file.deploymentId || (file.clipData?.deployment_id as string) || undefined;
  const activeFileId = fileId || file.fileId || (file.clipData?.file_id as string) || undefined;

  const totalSpeciesCount = detections.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0B0B0B',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          color: '#fff',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
          overflow: 'hidden',
        },
      }}
    >
      {/* Header section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Green circular icon */}
          <Box
            sx={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: '1.5px solid #10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(16, 185, 129, 0.08)',
            }}
          >
            <WaveformIcon sx={{ color: '#10b981', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 800, color: 'rgba(255, 255, 255, 0.4)', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Audio Analyzer
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', mt: 0.1 }}>
              {file.fileName}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {canDownload && (
            <IconButton
              onClick={handleDownload}
              disabled={isDownloading}
              title={downloadFailed ? 'Download failed, click to retry' : 'Download audio'}
              aria-label="Download audio"
              sx={{
                color: downloadFailed ? '#F59E0B' : '#10B981',
                bgcolor: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '8px',
                p: '6px',
                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.18)', color: '#fff' },
              }}
            >
              {isDownloading ? <CircularProgress size={18} sx={{ color: '#10B981' }} /> : <DownloadIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
          <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.4)', '&:hover': { color: '#fff' } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Main dialog layout */}
      <DialogContent
        sx={{
          p: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.7fr 1.1fr' },
          minHeight: '480px',
          maxHeight: '75vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {/* LEFT COLUMN: Waveform & Spectrogram visualizer */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            borderRight: '1px solid rgba(255, 255, 255, 0.05)',
            minWidth: 0,
            height: '100%',
          }}
        >
          {/* Waveform Visualization container */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, letterSpacing: '1px' }}>
                WAVEFORM
              </Typography>
              <Typography sx={{ fontSize: '9px', color: audioUnavailable ? '#F59E0B' : 'rgba(255, 255, 255, 0.4)', fontWeight: 700, letterSpacing: '1px' }}>
                {audioUnavailable ? 'AUDIO UNAVAILABLE' : duration}
              </Typography>
            </Box>

            <Box ref={waveformRef} sx={{ width: '100%', flex: 1, bgcolor: 'rgba(255, 255, 255, 0.01)', border: '1.5px solid rgba(255, 255, 255, 0.03)', borderRadius: '12px', overflow: 'hidden' }} />
          </Box>

          {/* Spectrogram Container */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, letterSpacing: '1px' }}>
                SPECTROGRAM
              </Typography>
            </Box>

            <Box sx={{ position: 'relative', width: '100%', flex: 1, bgcolor: '#040404', border: '1.5px solid rgba(255, 255, 255, 0.03)', borderRadius: '12px', overflow: 'hidden' }}>
              <Box 
                ref={spectrogramRef} 
                sx={{ 
                  width: '100%', 
                  height: '100%', 
                  '& > div': { height: '100% !important' },
                  '& canvas': { height: '100% !important' }
                }} 
              />
              
              {/* Live Playhead Overlay for Spectrogram */}
              <Box 
                ref={spectroPlayheadRef}
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '0%',
                  width: '2px',
                  bgcolor: '#fff',
                  boxShadow: '0 0 4px #fff, 0 0 10px #10b981',
                  zIndex: 10,
                  pointerEvents: 'none',
                  display: 'none',
                }}
              />
            </Box>
          </Box>

          {/* Bottom playback player controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'rgba(255, 255, 255, 0.01)',
              border: '1.5px solid rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              p: '8px 16px',
              mt: 0.5,
            }}
          >
            {/* Skip and Repeat Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.55)', '&:hover': { color: '#fff' } }}>
                <ReplayIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.55)', '&:hover': { color: '#fff' } }}>
                <SkipPrevIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Central Play/Pause Pulsing Button */}
            <Box
              onClick={togglePlay}
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                bgcolor: '#10b981',
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 0 10px rgba(16, 185, 129, 0.3)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 0 14px rgba(16, 185, 129, 0.5)',
                },
              }}
            >
              {isPlaying ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayIcon sx={{ fontSize: 20 }} />}
            </Box>

            {/* Next/Loop controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.55)', '&:hover': { color: '#fff' } }}>
                <SkipNextIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.55)', '&:hover': { color: '#fff' } }}>
                <RepeatIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Current Duration label */}
            <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
              {currentTime} / {duration}
            </Typography>

            {/* Volume and Settings */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100px' }}>
              <IconButton 
                onClick={toggleMute}
                sx={{ p: 0, color: (isMuted || volume === 0) ? '#f87171' : 'rgba(255, 255, 255, 0.45)', '&:hover': { color: '#fff' } }}
              >
                {isMuted || volume === 0 ? <MuteIcon sx={{ fontSize: 16 }} /> : <VolumeIcon sx={{ fontSize: 16 }} />}
              </IconButton>
              <Slider
                value={volume}
                onChange={handleVolumeChange}
                size="small"
                sx={{
                  color: '#10b981',
                  height: 3,
                  '& .MuiSlider-thumb': {
                    width: 6,
                    height: 6,
                    transition: '0.3s cubic-bezier(.47,0,.74,1.2)',
                    '&:before': { boxWhite: 'none' },
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: 'none',
                    },
                  },
                  '& .MuiSlider-rail': {
                    opacity: 0.15,
                    bgcolor: '#fff',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* RIGHT COLUMN: Detected Species List */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 2,
            minWidth: 0, 
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, letterSpacing: '1px' }}>
                SPECIES DETECTED
              </Typography>
              <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.4)', '&:hover': { color: '#fff' } }}>
                <FilterIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>

            {/* Checklist details mapping */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {detections.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.25)', fontSize: '12px', fontStyle: 'italic' }}>
                    No detections above threshold.
                  </Typography>
                </Box>
              ) : (
                detections.map((det, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    {/* Species Image avatar */}
                    <Box
                      component="img"
                      src={det.imageUrl}
                      alt={det.name}
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '6px',
                        objectFit: 'cover',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        bgcolor: 'rgba(255, 255, 255, 0.01)',
                      }}
                    />

                    {/* Info details and progress confidence bars */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.1, gap: 1 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {det.name}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#10b981', flexShrink: 0 }}>
                          {det.confidence}%
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '10px', fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.4)', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {det.scientificName}
                      </Typography>

                      {/* Progress confidence line */}
                      <LinearProgress
                        variant="determinate"
                        value={det.confidence}
                        sx={{
                          height: 3,
                          borderRadius: 1,
                          bgcolor: 'rgba(255,255,255,0.06)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#10b981',
                            borderRadius: 1,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                ))
              )}
            </Box>

            {detectedSpeciesName && (
              <SpeciesConfirmationCard
                key={`${activeDeploymentId}|${file.fileName}|${detectedSpeciesName}`}
                audioFile={file.fileName}
                detectedSpecies={detectedSpeciesName}
                deploymentId={activeDeploymentId}
                fileId={activeFileId}
              />
            )}
          </Box>

          {/* Total detections stat */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255, 255, 255, 0.05)', pt: 1.5 }}>
              <Typography sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600 }}>
                Total Detections
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#10b981', fontWeight: 700 }}>
                {totalSpeciesCount} species
              </Typography>
            </Box>
          </Box>
        </Box>

      </DialogContent>
    </Dialog>
  );
};

export default AudioAnalyzerPopup;
